import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

// Web port of app/src/lib/rollcall.ts. Same tables, same realtime channels —
// zero backend changes. Native + web can trigger and respond to the same
// roll call. Members on either platform see the overlay.

export type RollCallStatus = 'ok' | 'need_help' | 'on_my_way' | 'busy';

export type RollCall = {
  id: string;
  group_id: string;
  started_by: string;
  prompt: string | null;
  started_at: string;
  expires_at: string;
};

export type RollCallResponse = {
  roll_call_id: string;
  user_id: string;
  status: RollCallStatus;
  note: string | null;
  created_at: string;
  updated_at: string;
};

export async function startRollCall(groupId: string, selfId: string): Promise<RollCall> {
  const { data, error } = await supabase
    .from('roll_calls')
    .insert({ group_id: groupId, started_by: selfId })
    .select()
    .single();
  if (error) throw error;
  return data as RollCall;
}

export async function respondToRollCall(
  rollCallId: string,
  userId: string,
  status: RollCallStatus
): Promise<void> {
  const { error } = await supabase
    .from('roll_call_responses')
    .upsert(
      {
        roll_call_id: rollCallId,
        user_id: userId,
        status,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'roll_call_id,user_id' }
    );
  if (error) throw error;
}

export function useActiveRollCall(groupId: string | null) {
  const [rollCall, setRollCall] = useState<RollCall | null>(null);
  const [responses, setResponses] = useState<RollCallResponse[]>([]);
  const rollCallIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!groupId) {
      setRollCall(null);
      setResponses([]);
      rollCallIdRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('roll_calls')
        .select('*')
        .eq('group_id', groupId)
        .gt('expires_at', new Date().toISOString())
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn('roll call load failed', error.message);
        return;
      }
      if (data) {
        const rc = data as RollCall;
        setRollCall(rc);
        rollCallIdRef.current = rc.id;
        loadResponses(rc.id, setResponses);
      } else {
        setRollCall(null);
        setResponses([]);
        rollCallIdRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    const channel = supabase
      .channel(`roll-calls-${groupId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'roll_calls',
          filter: `group_id=eq.${groupId}`,
        },
        (payload) => {
          const row = payload.new as RollCall;
          setRollCall(row);
          rollCallIdRef.current = row.id;
          setResponses([]);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [groupId]);

  useEffect(() => {
    if (!rollCall) return;
    const channel = supabase
      .channel(`roll-call-responses-${rollCall.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'roll_call_responses',
          filter: `roll_call_id=eq.${rollCall.id}`,
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as RollCallResponse;
          if (!row?.user_id) return;
          setResponses((prev) => {
            const without = prev.filter((r) => r.user_id !== row.user_id);
            if (payload.eventType === 'DELETE') return without;
            return [...without, row];
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [rollCall?.id]);

  useEffect(() => {
    if (!rollCall) return;
    const ms = new Date(rollCall.expires_at).getTime() - Date.now();
    if (ms <= 0) {
      setRollCall(null);
      return;
    }
    const t = setTimeout(() => setRollCall(null), ms);
    return () => clearTimeout(t);
  }, [rollCall?.id, rollCall?.expires_at]);

  return { rollCall, responses };
}

async function loadResponses(
  rollCallId: string,
  setResponses: (r: RollCallResponse[]) => void
) {
  const { data, error } = await supabase
    .from('roll_call_responses')
    .select('*')
    .eq('roll_call_id', rollCallId);
  if (!error) setResponses((data ?? []) as RollCallResponse[]);
}

export function statusLabel(s: RollCallStatus): string {
  switch (s) {
    case 'ok':
      return 'OK';
    case 'need_help':
      return 'Need help';
    case 'on_my_way':
      return 'On my way';
    case 'busy':
      return 'Busy';
  }
}
